import { ReactText } from 'react';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { getAppInsights } from './telemetry';
import { ITEM_NOT_FOUND } from 'src/assets/config/stringResources';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

export const handleError = (error: any, toastId?: ReactText) => {
  var errorMessage = '';
  if (typeof error === 'string') {
    errorMessage = error.toUpperCase();
  } else if (error as AxiosError) {
    // unhandled exception
    if (error.status === 500) {
      errorMessage = error.message || JSON.stringify(error.response.detail);
      window.location.href = '/Error';
    } else if (error.status === 403) {
      errorMessage = 'Permission denied!';
    } else {
      errorMessage = JSON.stringify(error.response || error.message) || 'Something went wrong for crew pay API.';
    }
  } else if (error instanceof Error) {
    errorMessage = error.message;
    window.location.href = '/Error';
  }
  if (errorMessage.length > 0) {
    if (toastId) {
      toast.update(toastId, { render: errorMessage, type: 'error', isLoading: false, autoClose: false, closeOnClick: true });
    } else {
      toast.error(errorMessage, { hideProgressBar: true, closeOnClick: true });
    }
  }
  var appInsights = getAppInsights();
  appInsights?.trackException({ error: error, severityLevel: SeverityLevel.Error });
}

export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export function isEmpty(value: string): value is string {
  return !value?.length;
}

export function isEmptyOrWhitespace(value: string): value is string {
  return !value?.length || !value.trim();
}

export const Equal = (left: string | null, right: string | null): boolean => {
  return left?.toString()?.toUpperCase()?.trim() === right?.toString()?.toUpperCase()?.trim()
}

export const toNewZealandCurrency = (value: number | null, digits?: number) => {
  return digits
    ? new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD', minimumFractionDigits: digits }).format(value ?? 0)
    : new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' }).format(value ?? 0);
}

export const toPercentage = (value: number) => {
  return new Intl.NumberFormat('en-NZ', { style: 'percent' }).format(value);
}

export const toKg = (value: number | null) => {
  return new Intl.NumberFormat('en-NZ', { style: 'unit', unit: 'kilogram' }).format(value ?? 0);
}

// For custom sort (sortType)
export const compareNumericString = (rowA, rowB, id, desc) => {
  let a = Number.parseFloat(rowA.values[id]);
  let b = Number.parseFloat(rowB.values[id]);
  if (Number.isNaN(a)) {  // Blanks and non-numeric strings to bottom
    a = desc ? -1 : 1;
  }
  if (Number.isNaN(b)) {
    b = desc ? -1 : 1;
  }
  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
}

export const compareWithField = (a, b, id, fieldName) => {
  const rowASortOrder = a.original[fieldName];
  const rowBSortOrder = b.original[fieldName];
  if (rowASortOrder && rowBSortOrder) {
    return rowASortOrder - rowBSortOrder;
  }
  return a.values[id].localeCompare(b.values[id])
}

export const compareWithCategory = (periodA, periodB) => {
  const a = Number.parseFloat((periodA?.category ?? '').replace('Period', ''));
  const b = Number.parseFloat((periodB?.category ?? '').replace('Period', ''));
  if (Number.isNaN(a) || Number.isNaN(b)) {
    return -1
  }
  return a - b;
}

export const showErrorNotification = (message: string) => {
  toast.error(message, { hideProgressBar: true, autoClose: false });
}

export const showNotFoundErrorNotification = (code: string | null) => {
  showErrorNotification(ITEM_NOT_FOUND(code));
}

export const showWarningNotification = (message: string) => {
  toast.warning(message, { hideProgressBar: true, autoClose: 5000 });
}